import React from 'react';
import { Dialog, Flex, Select } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';

import { TransactionConfirmDialog } from '../transaction-confirm-dialog';
import { useTransactions } from '../transactions.provider';
import { Transaction, TransactionStatusValue } from '../types';

import './transaction-actions-menu.scss';

type TransactionStatusSelectValue = '1' | '2' | '3' | '4';

const TRANSACTION_STATUSES: Array<{ value: TransactionStatusSelectValue; label: string }> = [
   { value: '2', label: 'В процесі' },
   { value: '3', label: 'Не оплачено' },
   { value: '1', label: 'Активований' },
   { value: '4', label: 'Деактивований' },
];

const toStatusValue = (status: Transaction['status']): TransactionStatusSelectValue => {
   if (status === 1 || status === 2 || status === 3 || status === 4) {
      return String(status) as TransactionStatusSelectValue;
   }

   return '4';
};

const getStatusLabel = (status: TransactionStatusSelectValue): string =>
   TRANSACTION_STATUSES.find((item) => item.value === status)?.label ?? '';

const toApiStatus = (status: TransactionStatusSelectValue): TransactionStatusValue =>
   Number(status) as TransactionStatusValue;

const getStatusColorClass = (status: TransactionStatusSelectValue): string => {
   if (status === '1') {
      return 'transaction-actions-menu__select-trigger--activated';
   }

   if (status === '4') {
      return 'transaction-actions-menu__select-trigger--deactivated';
   }

   if (status === '3') {
      return 'transaction-actions-menu__select-trigger--unpaid';
   }

   return 'transaction-actions-menu__select-trigger--pending';
};

const TransactionActionsMenuView = ({ transaction }: { transaction: Transaction }) => {
   const transactionsStore = useTransactions();
   const transactionStatus = toStatusValue(transaction.status);
   const [open, setOpen] = React.useState<boolean>(false);
   const [selectedStatus, setSelectedStatus] =
      React.useState<TransactionStatusSelectValue>(transactionStatus);
   const [pendingStatus, setPendingStatus] =
      React.useState<TransactionStatusSelectValue | null>(null);
   const confirmedCloseRef = React.useRef(false);

   React.useEffect(() => {
      if (!open) {
         setSelectedStatus(transactionStatus);
      }
   }, [open, transactionStatus]);

   const resetToTransactionStatus = () => {
      setSelectedStatus(transactionStatus);
      setPendingStatus(null);
   };

   const closeAfterSuccess = (status: TransactionStatusSelectValue) => {
      confirmedCloseRef.current = true;
      setSelectedStatus(status);
      setPendingStatus(null);
      setOpen(false);
   };

   const onStatusChange = (value: string) => {
      const nextStatus = value as TransactionStatusSelectValue;

      if (nextStatus === transactionStatus) {
         resetToTransactionStatus();
         return;
      }

      setSelectedStatus(nextStatus);
      setPendingStatus(nextStatus);
      setOpen(true);
   };

   const onConfirmStatusChange = () => {
      if (!pendingStatus) {
         return;
      }

      transactionsStore.updateTransactionStatus({
         id: transaction.id,
         status: toApiStatus(pendingStatus),
         onSuccess: (resp) => {
            closeAfterSuccess(pendingStatus);
            toast.success(resp.message);
         },
         onError: (message) => {
            toast.error(message);
         },
      });
   };

   return (
      <Flex className="transaction-actions-menu" gap="1" align="center" justify="center">
         <Select.Root
            size="2"
            value={selectedStatus}
            disabled={transactionsStore.actionLoading}
            onValueChange={onStatusChange}
         >
            <Select.Trigger
               color="indigo"
               aria-label="Change transaction status"
               className={`transaction-actions-menu__select-trigger ${getStatusColorClass(
                  selectedStatus
               )}`}
            />
            <Select.Content color="blue" position="popper">
               {TRANSACTION_STATUSES.map((status) => (
                  <Select.Item key={status.value} value={status.value}>
                     {status.label}
                  </Select.Item>
               ))}
            </Select.Content>
         </Select.Root>

         <Dialog.Root
            open={open}
            onOpenChange={(nextOpen) => {
               if (!nextOpen) {
                  if (confirmedCloseRef.current) {
                     confirmedCloseRef.current = false;
                  } else {
                     resetToTransactionStatus();
                  }
               }
               setOpen(nextOpen);
            }}
         >
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(event) => event.preventDefault()}
            >
               {pendingStatus && (
                  <TransactionConfirmDialog
                     title="Change Payment Status"
                     message={`Do you want to change this payment status to ${getStatusLabel(
                        pendingStatus
                     )}?`}
                     confirmLabel="Confirm"
                     confirmColor="indigo"
                     loading={transactionsStore.updateTransactionStatusState.status === 'pending'}
                     onConfirm={onConfirmStatusChange}
                  />
               )}
            </Dialog.Content>
         </Dialog.Root>
      </Flex>
   );
};

export const TransactionActionsMenu = observer(TransactionActionsMenuView);
